import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/auth.js';
import { ApiError } from '../api/client.js';

const AuthContext = createContext(null);

function readRole(user) {
  if (!user) return null;
  if (user.roleId && typeof user.roleId === 'object' && user.roleId.name) return user.roleId.name;
  if (user.role) return user.role;
  return null;
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    status: 'loading', // loading | authenticated | anonymous
    user: null,
    permissions: [],
    storeSelection: null
  });

  const hydrate = useCallback(async () => {
    try {
      const data = await authApi.me();
      setState({
        status: 'authenticated',
        user: data.user,
        permissions: data.permissions || [],
        storeSelection: data.storeSelection || null
      });
    } catch (err) {
      if (err instanceof ApiError && (err.statusCode === 401 || err.statusCode === 403)) {
        setState({ status: 'anonymous', user: null, permissions: [], storeSelection: null });
        return;
      }
      // Treat any other failure as anonymous so the UI degrades to login.
      setState({ status: 'anonymous', user: null, permissions: [], storeSelection: null });
    }
  }, []);

  useEffect(() => { hydrate(); }, [hydrate]);

  const applyAuthenticated = useCallback((data) => {
    setState({
      status: 'authenticated',
      user: data.user,
      permissions: data.permissions || [],
      storeSelection: data.storeSelection || null
    });
    return data;
  }, []);

  const login = useCallback(async ({ email, password, mfaCode, passkeyResponse }) => {
    const data = await authApi.login({ email, password, mfaCode, passkeyResponse });
    return applyAuthenticated(data);
  }, [applyAuthenticated]);

  const completePasskeyLogin = useCallback(async ({ challengeId, response }) => {
    const data = await authApi.passkeyLoginFinish({ challengeId, response });
    return applyAuthenticated(data);
  }, [applyAuthenticated]);

  const selectStore = useCallback(async (storeId) => {
    const data = await authApi.selectStore(storeId);
    setState((prev) => ({ ...prev, storeSelection: data.storeSelection || prev.storeSelection }));
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    setState({ status: 'anonymous', user: null, permissions: [], storeSelection: null });
  }, []);

  const value = useMemo(() => {
    const role = readRole(state.user);
    return {
      status: state.status,
      user: state.user,
      role,
      permissions: state.permissions,
      storeSelection: state.storeSelection,
      activeStoreId: (state.storeSelection && state.storeSelection.activeStoreId) || null,
      needsStoreSelection: Boolean(state.storeSelection && state.storeSelection.required),
      isAuthenticated: state.status === 'authenticated',
      hasPermission: (key) => state.permissions.includes(key) || state.permissions.includes('*'),
      hasAnyPermission: (keys) => keys.some((k) => state.permissions.includes(k)) || state.permissions.includes('*'),
      login,
      completePasskeyLogin,
      selectStore,
      logout,
      refresh: hydrate
    };
  }, [state, login, completePasskeyLogin, selectStore, logout, hydrate]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
